/** Tipos de consulta na agenda — padrão geral vs. fisioterapia (por unidade). */

export const COMMITMENT_APPOINTMENT_TYPE = 'commitment';

export type AppointmentTypeOption = { value: string; label: string };

export type CustomAppointmentType = { key: string; label: string; color: string; icon?: string };

export type BuiltinAppointmentTypeOverride = {
  label?: string;
  color?: string;
  icon?: string;
};

const COMMITMENT_OPTION: AppointmentTypeOption = {
  value: COMMITMENT_APPOINTMENT_TYPE,
  label: 'Compromisso',
};

export const DEFAULT_APPOINTMENT_TYPE_OPTIONS: AppointmentTypeOption[] = [
  { value: 'consultation', label: 'Consulta' },
  { value: 'procedure', label: 'Procedimento' },
  { value: 'return', label: 'Retorno' },
  { value: 'exam', label: 'Exame' },
  { value: 'emergency', label: 'Emergência' },
  { value: 'surgery', label: 'Cirurgia' },
  { value: 'courtesy', label: 'Cortesia' },
];

/** Lista usada quando `clinic_medical_specialty` = fisioterapia. */
export const FISIOTERAPIA_APPOINTMENT_TYPE_OPTIONS: AppointmentTypeOption[] = [
  { value: 'pilates', label: 'Pilates' },
  { value: 'rpg', label: 'RPG' },
  { value: 'reabilitacao', label: 'REABILITAÇÃO' },
  { value: 'lpf', label: 'LPF' },
  { value: 'lpf_pilates', label: 'LPF/Pilates' },
  { value: 'estetica', label: 'Estética' },
  { value: 'avaliacao', label: 'Avaliação' },
  { value: 'reavaliacao', label: 'Reavaliação' },
  { value: 'limpeza_de_pele', label: 'Limpeza de pele' },
  { value: 'pilates_personal', label: 'Pilates Personal' },
];

function optionsToLabelMap(options: AppointmentTypeOption[]): Record<string, string> {
  return Object.fromEntries(options.map((o) => [o.value, o.label]));
}

export const DEFAULT_APPOINTMENT_TYPE_LABELS = optionsToLabelMap(DEFAULT_APPOINTMENT_TYPE_OPTIONS);
export const FISIOTERAPIA_APPOINTMENT_TYPE_LABELS = optionsToLabelMap(FISIOTERAPIA_APPOINTMENT_TYPE_OPTIONS);

/** União para relatórios, lembretes e registros legados. */
export const ALL_APPOINTMENT_TYPE_LABELS: Record<string, string> = {
  ...DEFAULT_APPOINTMENT_TYPE_LABELS,
  ...FISIOTERAPIA_APPOINTMENT_TYPE_LABELS,
  [COMMITMENT_APPOINTMENT_TYPE]: COMMITMENT_OPTION.label,
  teleconsultation: 'Teleconsulta',
  other: 'Outro',
};

export function isFisioterapiaClinic(specialty: string | null | undefined): boolean {
  return String(specialty || '').trim().toLowerCase() === 'fisioterapia';
}

export function getAppointmentTypeOptions(
  specialty: string | null | undefined,
  custom: CustomAppointmentType[] = [],
): AppointmentTypeOption[] {
  const base = isFisioterapiaClinic(specialty)
    ? FISIOTERAPIA_APPOINTMENT_TYPE_OPTIONS
    : DEFAULT_APPOINTMENT_TYPE_OPTIONS;
  const customOpts = custom.map((t) => ({ value: t.key, label: t.label }));
  const withoutDup = customOpts.filter((o) => !base.some((b) => b.value === o.value));
  return [...base, ...withoutDup, COMMITMENT_OPTION];
}

export function getAppointmentTypeLabelsMap(
  specialty: string | null | undefined,
  custom: CustomAppointmentType[] = [],
): Record<string, string> {
  return optionsToLabelMap(getAppointmentTypeOptions(specialty, custom));
}

export function customAppointmentTypeColorMap(custom: CustomAppointmentType[]): Record<string, string> {
  return Object.fromEntries(custom.map((t) => [t.key, t.color]));
}

export function getDefaultAppointmentTypeKey(specialty: string | null | undefined): string {
  return isFisioterapiaClinic(specialty) ? 'pilates' : 'consultation';
}

let runtimeCustomTypeLabels: Record<string, string> = {};
let runtimeBuiltinOverrideLabels: Record<string, string> = {};
let runtimeBuiltinOverrideColors: Record<string, string> = {};
let runtimeBuiltinOverrideIcons: Record<string, string> = {};
let runtimeCustomTypeIcons: Record<string, string> = {};

/** Atualizado ao carregar configurações da unidade (tipos personalizados). */
export function setRuntimeCustomAppointmentTypeLabels(labels: Record<string, string>): void {
  runtimeCustomTypeLabels = labels;
}

export function setRuntimeCustomAppointmentTypeIcons(icons: Record<string, string>): void {
  runtimeCustomTypeIcons = icons;
}

export function setRuntimeBuiltinAppointmentTypeOverrides(
  overrides: Record<string, BuiltinAppointmentTypeOverride>,
): void {
  runtimeBuiltinOverrideLabels = {};
  runtimeBuiltinOverrideColors = {};
  runtimeBuiltinOverrideIcons = {};
  for (const [key, o] of Object.entries(overrides || {})) {
    if (o.label) runtimeBuiltinOverrideLabels[key] = o.label;
    if (o.color) runtimeBuiltinOverrideColors[key] = o.color;
    if (o.icon) runtimeBuiltinOverrideIcons[key] = o.icon;
  }
}

export function getRuntimeAppointmentTypeIconId(typeKey: string): string | undefined {
  const key = String(typeKey || '').trim();
  return runtimeCustomTypeIcons[key] || runtimeBuiltinOverrideIcons[key];
}

export function translateAppointmentTypeLabel(value: unknown): string {
  const raw = value == null ? '' : String(value).trim();
  if (!raw) return isFisioterapiaClinic(null) ? 'Pilates' : 'Consulta';
  return (
    runtimeCustomTypeLabels[raw]
    || runtimeBuiltinOverrideLabels[raw]
    || ALL_APPOINTMENT_TYPE_LABELS[raw]
    || raw
  );
}

export function isCommitmentAppointmentType(type?: string | null): boolean {
  return type === COMMITMENT_APPOINTMENT_TYPE;
}

/** Cores na grade da agenda (fisioterapia). */
export const FISIOTERAPIA_APPOINTMENT_TYPE_COLORS: Record<string, string> = {
  pilates: '#0d9488',
  rpg: '#0891b2',
  reabilitacao: '#2563eb',
  lpf: '#7c3aed',
  lpf_pilates: '#6d28d9',
  estetica: '#db2777',
  avaliacao: '#ea580c',
  reavaliacao: '#c2410c',
  limpeza_de_pele: '#ec4899',
  pilates_personal: '#059669',
  [COMMITMENT_APPOINTMENT_TYPE]: '#64748b',
};

export const DEFAULT_APPOINTMENT_TYPE_COLORS: Record<string, string> = {
  consultation: '#14b8a6',
  /** Indigo — distinto do teal da consulta na grade. */
  return: '#6366f1',
  procedure: '#0891B2',
  exam: '#f59e0b',
  emergency: '#ef4444',
  surgery: '#ec4899',
  courtesy: '#a855f7',
  [COMMITMENT_APPOINTMENT_TYPE]: '#64748b',
};

let runtimeCustomTypeColors: Record<string, string> = {};

export function setRuntimeCustomAppointmentTypeColors(colors: Record<string, string>): void {
  runtimeCustomTypeColors = colors;
}

export function getAppointmentTypeColor(typeKey: string, _specialty?: string | null): string {
  const key = String(typeKey || '').trim();
  if (key && runtimeCustomTypeColors[key]) return runtimeCustomTypeColors[key];
  if (key && runtimeBuiltinOverrideColors[key]) return runtimeBuiltinOverrideColors[key];
  const palette = { ...DEFAULT_APPOINTMENT_TYPE_COLORS, ...FISIOTERAPIA_APPOINTMENT_TYPE_COLORS };
  if (!key) return palette.consultation;
  return palette[key] || palette.consultation || '#14b8a6';
}
